import {
    DndContext,
    DragEndEvent,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import classNames from 'classnames';
import React, { useState } from 'react';
import { FaRegTrashAlt } from 'react-icons/fa';
import { HiOutlineInformationCircle } from 'react-icons/hi2';
import { MdAdd, MdDragHandle } from 'react-icons/md';
import { Badge, Button, cn } from 'rizzui';
import CustomSelectApiHookForm from '../../../components/forms/CustomSelectApiHookForms';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import FormGroup from '../../../layout/shared/form-group';
import { TutorService } from '../../../services/tutor/tutorService';

interface ProductTutorsFormProps {
    formik: any;
    productId?: string | null;
    className?: string;
}

interface SortableTutorRowProps {
    tutorItem: any;
    index: number;
    canAssignTutors: boolean;
    onRemove: () => void;
}

const itemSortableId = (item: any, index: number): string =>
    item?.productHasTutorId ?? item?.tutorId ?? `new-${index}`;

const tutorTypeLabel = (tutorType?: string) => {
    if (tutorType === 'internal') return 'Interno';
    if (tutorType === 'external') return 'Externo';
    return tutorType ?? '';
};

const tutorDisplayName = (tutorItem: any): string => {
    if (tutorItem?.label) {
        return tutorItem.label.replace(/\s*\([^)]*\)\s*$/, '').trim();
    }
    const tutor = tutorItem?.tutor;
    if (tutor?.name) {
        return `${tutor.name} ${tutor.lastName ?? ''}`.trim();
    }
    return '—';
};

const SortableTutorRow: React.FC<SortableTutorRowProps> = ({ tutorItem, index, canAssignTutors, onRemove }) => {
    const sortableId = itemSortableId(tutorItem, index);
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortableId });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={cn(
                'grid grid-cols-2 md:grid-cols-6 gap-4 pb-4 items-center',
                'border-b border-gray-200'
            )}
        >
            <div className="flex items-center gap-2">
                {canAssignTutors && (
                    <button
                        type="button"
                        aria-label="Reordenar"
                        className="cursor-grab text-gray-500"
                        {...attributes}
                        {...listeners}
                    >
                        <MdDragHandle className="h-5 w-5" />
                    </button>
                )}
                <div className="text-sm font-medium">
                    Prioridad <span className="font-bold">{index + 1}</span>
                </div>
            </div>

            <div className="col-span-2">
                <p className="text-sm text-gray-900">{tutorDisplayName(tutorItem)}</p>
            </div>

            <div className="flex items-center">
                {tutorItem?.tutorType && (
                    <Badge variant="outline">{tutorTypeLabel(tutorItem.tutorType)}</Badge>
                )}
            </div>

            <div className="flex justify-end items-center col-start-6">
                {canAssignTutors && (
                    <Button type="button" color="danger" size="sm" onClick={onRemove}>
                        <FaRegTrashAlt />
                    </Button>
                )}
            </div>
        </div>
    );
};

const ProductTutorsForm: React.FC<ProductTutorsFormProps> = ({ formik, productId, className }) => {
    const { userCan } = usePrivilege();
    const canAssignTutors = Boolean(productId);

    const fetchTutorOptions = async (filters: any = {}) => {
        if (!productId) {
            return [];
        }

        const service = new TutorService();
        const response = await service.getTutorsForSelect({
            limit: 999999,
            filter_filters: { ...filters, active: true, product: productId },
        });
        const fetchedData = response.getResponseData()?.data;
        if (!fetchedData?.data) return [];
        return fetchedData.data.map((t: { id: string; name: string; lastName: string; nifCif: string; tutorType?: string }) => ({
            value: t.id,
            label: `${t.name} ${t.lastName} (${t.nifCif})`,
            object: t,
        }));
    };

    const [newTutor, setNewTutor] = useState<{ tutorId: string; label: string; tutorType: string }>({
        tutorId: '',
        label: '',
        tutorType: '',
    });

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const assignedTutors: any[] = formik.values.assignedTutors ?? [];
    const itemsIds: string[] = assignedTutors.map((t, idx) => itemSortableId(t, idx));
    const assignedTutorIds = assignedTutors.map((t: any) => t?.tutorId).filter(Boolean);

    const newTutorFormikShim = {
        values: { newTutorId: newTutor.tutorId || null },
        setFieldValue: (_: string, value: any) => {
            setNewTutor((prev) => ({ ...prev, tutorId: value ?? '' }));
        },
    };

    const handleDragEnd = (event: DragEndEvent) => {
        if (!canAssignTutors) return;

        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = itemsIds.indexOf(String(active.id));
        const newIndex = itemsIds.indexOf(String(over.id));
        if (oldIndex === -1 || newIndex === -1) return;

        const reordered = arrayMove(assignedTutors, oldIndex, newIndex);
        formik.setFieldValue('assignedTutors', reordered);
    };

    const handleRemove = (index: number) => {
        const updated = assignedTutors.filter((_: any, i: number) => i !== index);
        formik.setFieldValue('assignedTutors', updated);
    };

    const addNewTutor = () => {
        if (!newTutor.tutorId) return;
        const next = [...assignedTutors, { ...newTutor }];
        formik.setFieldValue('assignedTutors', next);
        setNewTutor({ tutorId: '', label: '', tutorType: '' });
    };

    return (
        <FormGroup
            title="Tutores asignados"
            description="Solo puedes priorizar tutores que ya tengan esta acción formativa en su ficha. Arrastra para cambiar la prioridad de asignación."
            className={classNames('pt-6', className)}
            titleCols="@md:col-span-2"
            childCols="@md:col-span-10 md:grid-cols-1"
        >
            {!canAssignTutors && (
                <div className="flex items-center gap-2 text-amber-600 text-sm mb-4">
                    <HiOutlineInformationCircle size={18} className="flex-shrink-0" />
                    <span>Guarda la acción formativa y asigna los tutores desde su ficha antes de priorizarlos aquí.</span>
                </div>
            )}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={itemsIds} strategy={verticalListSortingStrategy}>
                    {assignedTutors.map((tutorItem: any, index: number) => (
                        <SortableTutorRow
                            key={itemsIds[index]}
                            tutorItem={tutorItem}
                            index={index}
                            canAssignTutors={canAssignTutors}
                            onRemove={() => handleRemove(index)}
                        />
                    ))}
                </SortableContext>
            </DndContext>

            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-4">
                <div className="col-span-2 space-y-2">
                    <CustomSelectApiHookForm
                        id="newTutorId"
                        label="Nuevo tutor"
                        formik={newTutorFormikShim}
                        fetchOptions={async (searchText: string | null) => {
                            const options = await fetchTutorOptions({ search_text: searchText });
                            return (options ?? []).filter((o: any) => !assignedTutorIds.includes(o.value));
                        }}
                        setValue={(value: any) => {
                            const option = value?.object ?? null;
                            const tutor = option?.object ?? null;
                            setNewTutor({
                                tutorId: value?.id ?? option?.value ?? '',
                                label: value?.name ?? option?.label ?? '',
                                tutorType: tutor?.tutorType ?? '',
                            });
                        }}
                        error={undefined}
                        minInputLength={2}
                        containerClassName=""
                        isDisabled={!canAssignTutors || !userCan('get_tutors_for_select', 'tutors')}
                    />
                </div>

                <div className="flex justify-end items-end">
                    <Button
                        type="button"
                        color="secondary"
                        size="sm"
                        disabled={!canAssignTutors || newTutor.tutorId === ''}
                        onClick={addNewTutor}
                    >
                        <MdAdd className="h-5 w-5" />
                    </Button>
                </div>
            </div>
        </FormGroup>
    );
};

export default ProductTutorsForm;
